import { Box, Typography, useTheme,  Grid, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import DecDistributionChart from "../../components/charts/DecDistributionChart";
import DecDaoHistoryChart from "../../components/charts/DecDaoHistoryChart";
import DecDailyBurnsChart from "../../components/charts/DecDailyBurnsChart";
import DecBurnCategoriesChart from "../../components/charts/DecBurnCategoriesChart";
import DecSupplyChart from "../../components/charts/DecSupplyChart";
import DecTokenStats from "../../components/display/DecTokenStats";
import DecDailyStats from "../../components/display/DecDailyStats";
import DonateModal from "../../components/modals/DonateModal";
import { useState } from "react";
import BurnSpsModal from "../../components/modals/BurnSpsModal";
import ErrorIcon from '@mui/icons-material/Error';
import Tooltip from '@mui/material/Tooltip';
import PremiumContent from "../../components/premium/PremiumContent";

const DecDashboard = ({ onDonationSuccess }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [donateModalOpen, setDonateModalOpen] = useState(false);
  const [burnModalOpen, setBurnModalOpen] = useState(false);
  const [decDisclaimer, setDecDisclaimer] = useState("");
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Handle donation modal
  const handleDonateClick = () => {
    setDonateModalOpen(true);
  };

  const handleDonateModalClose = () => {
    setDonateModalOpen(false);
  };

  const handleDonateSuccess = (donationData) => {
    console.log("Donation successful:", donationData);
    
    if (onDonationSuccess) {
      onDonationSuccess(donationData);
    }
  };

  const handleBurnModalClose = () => {
    setBurnModalOpen(false);
  };

  // Callback für DecDistributionChart, um Disclaimer-Text zu holen
  const handleDecDisclaimer = (text) => {
    setDecDisclaimer(text);
  };

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      {/* GRID & CHARTS - Using responsive Grid system */}
      <Grid 
        container 
        spacing={isMobile ? 1 : 2}
      >
        {/* DEC Distribution Chart */}
        <Grid item xs={12} sm={12} md={5} lg={5}>
          <PremiumContent 
            featureId="DEC_DISTRIBUTION_CHART"
            height={isMobile ? "320px" : "420px"}
          >
            <Box
              backgroundColor={colors.primary[400]}
              p={isMobile ? "15px" : "30px"}
              height="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              sx={{ paddingTop: 4 }}
            >
              <Box display="flex" alignItems="center" sx={{ marginBottom: isMobile ? "32px" : "32px" }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  DEC Token Distribution
                </Typography>
                <Tooltip title={decDisclaimer || "This chart shows the distribution of active DEC tokens. Values are calculated by grouping different wallet addresses and may differ from official figures. Data is sourced directly from the Hive blockchain."} placement="right">
                  <ErrorIcon sx={{ color: colors.redAccent[400], ml: 1, fontSize: isMobile ? 18 : 22, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box flex="1 1 0" minHeight={isMobile ? "200px" : "300px"} display="flex" flexDirection="column" justifyContent="flex-start">
                <DecDistributionChart onDisclaimer={handleDecDisclaimer} />
              </Box>
            </Box>
          </PremiumContent>
        </Grid>

        {/* Daily Stats */}
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box
            backgroundColor={colors.primary[400]}
            p={isMobile ? "15px" : "20px"}
            borderRadius="8px"
            height={isMobile ? "auto" : "420px"}
          >
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" sx={{ marginBottom: isMobile ? "8px" : "15px" }}>
              Daily Changes
            </Typography>
            <DecDailyStats />
          </Box>
        </Grid>
        
        {/* DEC Token Stats */}
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Box
            backgroundColor={colors.primary[400]}
            p={isMobile ? "15px" : "30px"}
            borderRadius="8px"
            height={isMobile ? "auto" : "420px"}
          >
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" sx={{ marginBottom: isMobile ? "8px" : "15px" }}>
              DEC Token Stats
            </Typography>
            <DecTokenStats />
          </Box>
        </Grid>

        {/* DAO DEC History - Full Width */}
        <Grid item xs={12}>
          <PremiumContent 
            featureId="DEC_DAO_HISTORY"
            height={isMobile ? "300px" : "380px"}
          >
            <Box
              backgroundColor={colors.primary[400]}
              p={isMobile ? "15px" : "30px"}
              height="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              sx={{ paddingTop: 4 }}
            >
              <Box display="flex" alignItems="center" sx={{ marginBottom: isMobile ? "16px" : "24px" }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  DAO DEC Daily Changes
                </Typography>
                <Tooltip title="This chart shows the daily changes in DEC balance held by DAO wallets. The DAO now manages most operations. Data is updated daily from blockchain sources." placement="right">
                  <ErrorIcon sx={{ color: colors.greenAccent[400], ml: 1, fontSize: isMobile ? 18 : 22, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box flex="1 1 0" minHeight={isMobile ? "200px" : "280px"}>
                <DecDaoHistoryChart />
              </Box>
            </Box>
          </PremiumContent>
        </Grid>

        {/* DEC Daily Burns - Full Width */}
        <Grid item xs={12}>
          <PremiumContent 
            featureId="DEC_DAILY_BURNS"
            height={isMobile ? "300px" : "380px"}
          >
            <Box
              backgroundColor={colors.primary[400]}
              p={isMobile ? "15px" : "30px"}
              height="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              sx={{ paddingTop: 4 }}
            >
              <Box display="flex" alignItems="center" sx={{ marginBottom: isMobile ? "16px" : "24px" }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  DEC Daily Burns
                </Typography>
                <Tooltip title="This chart shows the total amount of DEC tokens burned over time. Burned tokens are permanently removed from circulation." placement="right">
                  <ErrorIcon sx={{ color: colors.redAccent[400], ml: 1, fontSize: isMobile ? 18 : 22, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box flex="1 1 0" minHeight={isMobile ? "200px" : "280px"}>
                <DecDailyBurnsChart />
              </Box>
            </Box>
          </PremiumContent>
        </Grid>

        {/* DEC Burn Categories - Full Width */}
        <Grid item xs={12}>
          <PremiumContent 
            featureId="DEC_BURN_CATEGORIES"
            height={isMobile ? "350px" : "450px"}
          >
            <Box
              backgroundColor={colors.primary[400]}
              p={isMobile ? "15px" : "30px"}
              height="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              sx={{ paddingTop: 4 }}
            >
              <Box display="flex" alignItems="center" sx={{ marginBottom: isMobile ? "16px" : "24px" }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  DEC Burn Categories
                </Typography>
                <Tooltip title="This chart shows how DEC tokens are being burned across different game features and activities. Data includes shop purchases, market fees, guild stores, and more." placement="right">
                  <ErrorIcon sx={{ color: colors.redAccent[400], ml: 1, fontSize: isMobile ? 18 : 22, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box flex="1 1 0" minHeight={isMobile ? "250px" : "350px"}>
                <DecBurnCategoriesChart />
              </Box>
            </Box>
          </PremiumContent>
        </Grid>

        {/* DEC Supply History - Full Width */}
        <Grid item xs={12}>
          <PremiumContent 
            featureId="DEC_SUPPLY_HISTORY"
            height={isMobile ? "350px" : "450px"}
          >
            <Box
              backgroundColor={colors.primary[400]}
              p={isMobile ? "15px" : "30px"}
              height="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              sx={{ paddingTop: 4 }}
            >
              <Box display="flex" alignItems="center" sx={{ marginBottom: isMobile ? "16px" : "24px" }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  Total DEC Supply History
                </Typography>
                <Tooltip title="This chart shows the total DEC supply over time. Total supply represents all DEC tokens in existence across all chains and wallets." placement="right">
                  <ErrorIcon sx={{ color: colors.blueAccent[400], ml: 1, fontSize: isMobile ? 18 : 22, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box flex="1 1 0" minHeight={isMobile ? "250px" : "350px"}>
                <DecSupplyChart />
              </Box>
            </Box>
          </PremiumContent>
        </Grid>
      </Grid>


      {/* Donate Modal */}
      <DonateModal
        open={donateModalOpen}
        onClose={handleDonateModalClose}
        onSuccess={handleDonateSuccess}
      />

      {/* Burn Modal */}
      <BurnSpsModal
        open={burnModalOpen}
        onClose={handleBurnModalClose}
        tokenType="DEC"
      />
    </Box>
  );
};

export default DecDashboard;